// 신청·문의관리 공유 타입 + SaaS 플랫폼/상태값 정의.
//  - 플랫폼: 고정 3개(boxs/chanbi/gnote). 그 아래 상품(SaasProduct)을 CRUD로 관리.
//  - 접수(Request): 문의(inquiry) / 신청(application) 2종. 상태셋이 종류별로 다르다.
//  - 1:1 스레드: RequestMessage(교회측/관리자측) + 파일 첨부(RequestAttachment).

export const PLATFORMS = ['boxs', 'chanbi', 'gnote'] as const;
export type Platform = (typeof PLATFORMS)[number];

/** 화면 표기용 라벨 (도메인) */
export const PLATFORM_LABELS: Record<Platform, string> = {
  boxs: 'boxs.kr',
  chanbi: 'chanbi.kr',
  gnote: 'gnote.kr',
};

export const REQUEST_KINDS = ['inquiry', 'application'] as const;
export type RequestKind = (typeof REQUEST_KINDS)[number];

export const KIND_LABELS: Record<RequestKind, string> = {
  inquiry: '문의',
  application: '신청',
};

/** 플랫폼별 허용 접수 유형 (chanbi.kr은 문의만 가능) */
export const PLATFORM_CAPABILITIES: Record<Platform, RequestKind[]> = {
  boxs: ['inquiry', 'application'],
  chanbi: ['inquiry'],
  gnote: ['inquiry', 'application'],
};

/** 문의 상태: 대기 → 답변완료 → 완료 → 종료 */
export const INQUIRY_STATUSES = ['대기', '답변완료', '완료', '종료'] as const;
/** 신청 상태: 대기 → 진행 → 완료 → 종료 */
export const APPLICATION_STATUSES = ['대기', '진행', '완료', '종료'] as const;

export const DEFAULT_STATUS = '대기';

export function statusesFor(kind: RequestKind): readonly string[] {
  return kind === 'application' ? APPLICATION_STATUSES : INQUIRY_STATUSES;
}

export function isPlatform(v: unknown): v is Platform {
  return typeof v === 'string' && (PLATFORMS as readonly string[]).includes(v);
}

export function platformAllows(platform: Platform, kind: RequestKind): boolean {
  return PLATFORM_CAPABILITIES[platform].includes(kind);
}

export interface SaasProduct {
  id: number;
  platform: Platform;
  name: string;
  description: string;
  price: number;
  isActive: boolean;
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
}

export interface RequestAttachment {
  id: number;
  requestId: number;
  messageId: number | null;
  originalName: string;
  size: number;
  mime: string;
  createdAt: string;
  /** 관리자용 다운로드 URL (서버에서 채움) */
  url: string;
}

export interface RequestMessage {
  id: number;
  requestId: number;
  sender: 'church' | 'admin';
  content: string;
  createdAt: string;
  attachments: RequestAttachment[];
}

export interface RequestSummary {
  id: number;
  kind: RequestKind;
  platform: Platform;
  productId: number | null;
  productName: string | null;
  churchName: string;
  contactName: string;
  contactPhone: string;
  contactEmail: string;
  title: string;
  status: string;
  source: string;
  messageCount: number;
  attachmentCount: number;
  /** 읽음수 (초기 0, 상태변경·스레드 갱신 시 0으로 초기화) */
  readCount: number;
  createdAt: string;
  updatedAt: string;
}

export interface RequestDetail extends RequestSummary {
  messages: RequestMessage[];
}

// ---- 입력 타입 ----
export type ProductInput = {
  platform: Platform;
  name: string;
  description?: string;
  price?: number;
  isActive?: boolean;
};

export type ProductPatch = Partial<ProductInput> & { sortOrder?: number };

/** 외부 사이트에서 접수하는 공개 신청/문의 입력 */
export type PublicRequestInput = {
  kind: RequestKind;
  platform: Platform;
  productId?: number | null;
  churchName: string;
  contactName?: string;
  contactPhone?: string;
  contactEmail?: string;
  title: string;
  content: string;
};

export type RequestStatusPatch = { status: string };
export type RequestReplyInput = { content: string };
